// src/services/product.js
// UPDATED VERSION with automatic CNN processing integration

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';
import { createOrGetRing } from './ringService';

// Map Firestore doc to app's product format
const mapFirestoreDocToProduct = (doc) => {
  const data = doc.data();
  
  // Extract from metadata if it exists, otherwise use root level
  const metadata = data.metadata || {};
  
  return {
    id: doc.id,
    name: metadata.name || data.name || '',
    description: metadata.description || data.description || '',
    price: metadata.price || data.price || '',
    availablePurchase: data.availablePurchase,
    imageUrls: data.imageUrls || [],
    
    // Convert arrays back to individual fields for the form
    metalType: metadata.metal?.[0] || data.metal?.[0] || '',
    gemstoneType: metadata.gemstone?.[0] || data.gemstone?.[0] || '',
    gemstoneColor: metadata.gemstone?.[1] || data.gemstone?.[1] || '',
    settingType: metadata.style?.[0] || data.style?.[0] || '',
    
    status: data.status || 'draft',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
};

// Filter out analytics/global documents that shouldn't surface in the products UI
const shouldIncludeAsProduct = (docSnapshot) => {
  const data = docSnapshot.data() || {};
  const hasGlobalIdPrefix =
    typeof docSnapshot.id === 'string' && docSnapshot.id.startsWith('jeweller_');
  const hasStyleProcessedFlag = Object.prototype.hasOwnProperty.call(
    data,
    'styleProcessed'
  );

  const allowedStatuses = ['active', 'live', 'draft'];
  const statusVal = data.status || 'active';
  const isAllowedStatus = allowedStatuses.includes(statusVal);
  const parentDeleted = data.parentProductDeleted === true;

  return (
    !hasGlobalIdPrefix &&
    !hasStyleProcessedFlag &&
    isAllowedStatus &&
    !parentDeleted
  );
};

// Map app's product to Firestore format
const mapProductToFirestoreDoc = (product) => {
  // Convert form fields to arrays for metadata
  const metal = [];
  if (product.metalType) metal.push(product.metalType);
  
  const gemstone = [];
  if (product.gemstoneType) gemstone.push(product.gemstoneType);
  if (product.gemstoneColor) gemstone.push(product.gemstoneColor);
  
  const style = [];
  if (product.settingType) style.push(product.settingType);
  
  return {
    name: product.name || '',
    description: product.description || '',
    price: product.price || '',
    imageUrls: product.imageUrls || [],
    status: product.status || 'draft',
    availablePurchase: product.availablePurchase === false ? false : true,
    
    // Store in metadata for CNN processing
    metadata: {
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      metal: metal,
      gemstone: gemstone,
      style: style,
    }
  };
};

// Helper function to trigger CNN processing for a product
async function triggerCNNProcessing(jewellerId, productId, productData) {
  try {
    console.log('[triggerCNNProcessing] start', {
      jewellerId,
      productId,
      hasImages: !!productData?.imageUrls?.length
    });
  
    // Only process if there are images
    if (!productData.imageUrls || productData.imageUrls.length === 0) {
      console.log('No images to process for CNN');
      return null;
    }
    
    // Use the first image for CNN analysis
    const primaryImageUrl = productData.imageUrls[0];
    
    // Use a stable source ID per product so reprocessing updates the same CNN ring doc.
    // If we already have a linked cnnRingId, preserve it.
    const sourceIdFromExistingRingId =
      typeof productData?.cnnRingId === 'string' && productData.cnnRingId.startsWith('jeweller_')
        ? productData.cnnRingId.slice('jeweller_'.length)
        : null;
    const sourceId = sourceIdFromExistingRingId || `${jewellerId}_product_${productId}`;
    
    // Convert form fields to arrays (same as mapper does)
    const metal = [];
    if (productData.metalType) metal.push(productData.metalType);
    
    const gemstone = [];
    if (productData.gemstoneType) gemstone.push(productData.gemstoneType);
    if (productData.gemstoneColor) gemstone.push(productData.gemstoneColor);
    
    const style = [];
    if (productData.settingType) style.push(productData.settingType);
    
    // Create metadata for the ring
    const metadata = {
      jewellerId,
      productId, // Link back to the product
      jewellerName: productData.jewellerName || 'Unknown Jeweller',
      source: 'jeweller_product', // Distinguish from manual uploads
      
      // Product details with properly formatted arrays
      name: productData.name || 'Untitled Ring',
      price: productData.price || null,
      metal: metal,
      gemstone: gemstone,
      style: style,
      description: productData.description || '',
      
      // Additional metadata
      uploadedAt: new Date().toISOString(),
      uploadedBy: jewellerId,
      autoProcessed: true // Flag that this was automatic
    };
    
    // Create or get ring in the unified system - this triggers CNN automatically
    const result = await createOrGetRing(
      'jeweller',
      sourceId,
      primaryImageUrl,
      metadata
    );

    console.log('[triggerCNNProcessing] createOrGetRing result', {
      productId,
      ringId: result?.ringId,
      isNew: result?.isNew
    });
    
    console.log(`CNN processing triggered for product ${productId}: ${result.ringId}`);
    
    // Optionally store the ring ID reference in the product document
    // This allows you to link products to their CNN analysis later
   const productRef = doc(db, 'rings', productId);
    console.log('[triggerCNNProcessing] updating product with cnnRingId', {
      productId,
      cnnRingId: result?.ringId
    });

    await updateDoc(productRef, {
      cnnRingId: result.ringId,            // always set it
      cnnProcessingTriggered: true,
      cnnTriggeredAt: Timestamp.now()
    });

    console.log('[triggerCNNProcessing] updateDoc success', { productId });
    
    return result;
    
  } catch (error) {
    // Don't fail the product creation if CNN processing fails
    console.error('[triggerCNNProcessing] error', {
      productId,
      errorName: error?.name,
      errorCode: error?.code,
      errorMessage: error?.message
    });console.error('Error triggering CNN processing:', error);
    return null;
  }
}

// Fetch all rings for a jeweller
export const getJewellerRings = async (jewellerId) => {
  try {
    if (!jewellerId) {
      console.error("jewellerId is required to fetch rings");
      throw new Error("jewellerId is required");
    }
    
    const ringsQuery = query(
      collection(db, 'rings'),
      where('jewellerId', '==', jewellerId),
      where('status', 'in', ['active', 'live', 'draft'])
    );
    
    const querySnapshot = await getDocs(ringsQuery);
    const rings = querySnapshot.docs
      .filter(shouldIncludeAsProduct)
      .map(mapFirestoreDocToProduct);
    
    return rings;
  } catch (error) {
    console.error('Error fetching jeweller rings:', error);
    throw error;
  }
};

// Create a new ring (with CNN integration)
export const createRing = async (jewellerId, productData) => {
  try {
    // Use the mapper to convert our app data to the correct Firestore format
    const newRingData = mapProductToFirestoreDoc(productData);
    
    // Create the product in the existing rings collection
    const newRingRef = await addDoc(collection(db, 'rings'), {
      ...newRingData,
      jewellerId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    const productId = newRingRef.id;
    
    // Automatically trigger CNN processing in the background
    // This is fire-and-forget - doesn't block product creation
    triggerCNNProcessing(jewellerId, productId, {
      ...productData,
      id: productId
    }).catch(error => {
      console.error('Background CNN processing failed:', error);
      // Error is logged but doesn't affect product creation
    });
    
    return productId;
  } catch (error) {
    console.error('Error creating ring:', error);
    throw error;
  }
};

// Update a ring (with CNN integration)
export const updateRing = async (ringId, productData) => {
  try {
    // Use the mapper to prepare the update data for Firestore
    const updatedRingData = mapProductToFirestoreDoc(productData);

    const ringDocRef = doc(db, 'rings', ringId);

    // Fetch existing data before the update so we can compare image order changes
    const existingDoc = await getDoc(ringDocRef);
    const existingData = existingDoc.data();
    const existingFirstImage = existingData?.imageUrls?.[0];
    const newFirstImage = productData.imageUrls?.[0];

    await updateDoc(ringDocRef, {
      ...updatedRingData,
      updatedAt: Timestamp.now()
    });
    
    // Check if images have changed and trigger CNN reprocessing
    // Only process if this is the first image or images have changed
    if (productData.imageUrls && productData.imageUrls.length > 0) {
      // If the first image has changed, trigger reprocessing
      if (newFirstImage && newFirstImage !== existingFirstImage) {
        console.log('Image changed, triggering CNN reprocessing');
        
        // Get jewellerId from existing document
        const jewellerId = existingData.jewellerId;
        
        triggerCNNProcessing(jewellerId, ringId, {
          ...productData,
          id: ringId,
          cnnRingId: existingData?.cnnRingId || productData?.cnnRingId || null
        }).catch(error => {
          console.error('Background CNN reprocessing failed:', error);
        });
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error updating ring:', error);
    throw error;
  }
};

// Delete a ring (also marks CNN ring as inactive)
export const deleteRing = async (ringId) => {
  try {
    // Get the ring document first to check for CNN ring ID
    const ringDocRef = doc(db, 'rings', ringId);
    const ringDoc = await getDoc(ringDocRef);
    
    if (ringDoc.exists()) {
      const ringData = ringDoc.data();
      
      // If there's an associated CNN ring, mark it as inactive
      if (ringData.cnnRingId) {
        try {
          const cnnRingRef = doc(db, 'rings', ringData.cnnRingId);
          await updateDoc(cnnRingRef, {
            status: 'inactive',
            deletedAt: Timestamp.now(),
            parentProductDeleted: true
          });
          const check = await getDoc(cnnRingRef);
          console.log('[deleteRing] cnnRing after update:', ringData.cnnRingId, check.exists() ? check.data().status : 'missing');
        } catch (error) {
          console.error('Error marking CNN ring as inactive:', error);
          // Continue with deletion even if this fails
        }
      }
    }
    
    try {
      // Fallback cleanup: mark ANY derived ring whose metadata.productId == ringId
      // (and optionally source == 'jeweller_product') as inactive/parent-deleted.
      const siblingsQuery = query(
        collection(db, 'rings'),
        where('metadata.productId', '==', ringId),
      );
      const siblingsSnap = await getDocs(siblingsQuery);
      const updates = siblingsSnap.docs.map(d =>
        updateDoc(d.ref, {
          status: 'inactive',
          parentProductDeleted: true,
          deletedAt: Timestamp.now()
        })
      );
      await Promise.all(updates);
    } catch (e) {
      console.error('Fallback sibling cleanup failed:', e);
      // Don’t block deletion if this fails.
    }

    // Delete the product
    await deleteDoc(ringDocRef);
    return true;
  } catch (error) {
    console.error('Error deleting ring:', error);
    throw error;
  }
};
